import {
  Accordion,
  Box,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaButton } from "@nens/ranalib";

import { RanaAccordionItem } from "@/components/rana-accordion-item";

import { MIN_CAPTURE_AREA_SIZE } from "../consts";
import { ExportFormat, ExportSettings, ExportSize, PDFOrientation } from "../types/index";

import { ExportExplanation } from "./export-explanation";

interface ExportSettingsPanelProps {
  settings: ExportSettings;
  updateFormat: (format: ExportFormat) => void;
  updateExportSize: (size: ExportSize) => void;
  updateOrientation: (orientation: PDFOrientation) => void;
  captureAreaWidth: number;
  captureAreaHeight: number;
  updateCaptureAreaSize: (width: number, height: number) => void;
  updateFileName: (fileName: string) => void;
  startExport: () => void;
  isExporting?: boolean;
}

export const ExportSettingsPanel: React.FC<ExportSettingsPanelProps> = ({
  settings,
  captureAreaWidth,
  captureAreaHeight,
  updateFormat,
  updateExportSize,
  updateOrientation,
  updateCaptureAreaSize,
  updateFileName,
  startExport,
  isExporting = false,
}) => {
  const intl = useIntl();
  const isPdf = settings.format === "pdf";
  // These string states are used to type into the width/heigth inputs and without:
  // 1. The min and max immediatly clamping the value, which would make it impossible to type a number outside the range
  // 2. The number inputs converting the value to a number, which would make it impossible to type a leading zero or a decimal point
  // Will be set to undefined on blur, so the inputs will show the actual square width/height again.
  const [widthString, setWidthString] = useState<string | undefined>(undefined);
  const [heightString, setHeightString] = useState<string | undefined>(undefined);

  return (
    <VStack bg="brand.cloudy" w="sm" align="stretch" rounded="base">
      <Accordion allowMultiple defaultIndex={[1]}>
        <ExportExplanation />
        <RanaAccordionItem
          mt={0}
          borderBottomWidth={0}
          roundedBottom="none"
          title={intl.formatMessage({
            id: "map.export_settings",
            defaultMessage: "Export Settings",
          })}
        >
          {/* Format Selection */}
          <FormControl>
            <FormLabel mt={2}>
              <FormattedMessage id="map.export_format" defaultMessage="Export Format" />
            </FormLabel>
            <RadioGroup
              value={settings.format}
              onChange={(value) => updateFormat(value as ExportFormat)}
            >
              <Stack direction="row">
                <Radio value="png">PNG</Radio>
                <Radio value="jpeg">JPEG</Radio>
                <Radio value="pdf">PDF</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>

          {/* Export Size / Orientation */}
          <FormControl>
            <FormLabel mt={2}>
              {isPdf ? (
                <FormattedMessage id="map.export_orientation" defaultMessage="Orientation" />
              ) : (
                <FormattedMessage id="map.export_size" defaultMessage="Export Size" />
              )}
            </FormLabel>
            {isPdf ? (
              <RadioGroup
                value={settings.orientation}
                onChange={(value) => updateOrientation(value as PDFOrientation)}
              >
                <Stack direction="row">
                  <Radio value="portrait">
                    <FormattedMessage id="map.portrait" defaultMessage="Portrait" />
                  </Radio>
                  <Radio value="landscape">
                    <FormattedMessage id="map.landscape" defaultMessage="Landscape" />
                  </Radio>
                </Stack>
              </RadioGroup>
            ) : (
              <RadioGroup
                value={settings.size}
                onChange={(value) => updateExportSize(value as ExportSize)}
              >
                <Stack direction="row">
                  <Radio value="1x">1x</Radio>
                  <Radio value="2x">2x</Radio>
                  <Radio value="3x">3x</Radio>
                </Stack>
              </RadioGroup>
            )}
          </FormControl>

          {/* Dimensions */}
          <FormControl>
            <FormLabel mt={2}>
              <FormattedMessage id="map.export_dimensions" defaultMessage="Dimensions (px)" />
            </FormLabel>
            <Grid templateColumns="1fr 1fr" gap={3}>
              <Box>
                <Text fontSize="sm" mt={1}>
                  <FormattedMessage id="map.width" defaultMessage="Width" />
                </Text>
                <NumberInput
                  value={widthString ?? captureAreaWidth}
                  onChange={(valueString, valueNumber) => {
                    setWidthString(valueString);
                    if (!isNaN(valueNumber)) {
                      updateCaptureAreaSize(valueNumber, captureAreaHeight);
                    }
                  }}
                  onBlur={() => setWidthString(undefined)}
                  min={MIN_CAPTURE_AREA_SIZE}
                  isDisabled={isExporting}
                >
                  <NumberInputField />
                </NumberInput>
              </Box>
              <Box>
                <Text fontSize="sm" mt={1}>
                  <FormattedMessage id="map.height" defaultMessage="Height" />
                </Text>
                <NumberInput
                  value={heightString ?? captureAreaHeight}
                  onChange={(valueString, valueNumber) => {
                    setHeightString(valueString);
                    if (!isNaN(valueNumber)) {
                      updateCaptureAreaSize(captureAreaWidth, valueNumber);
                    }
                  }}
                  onBlur={() => setHeightString(undefined)}
                  min={MIN_CAPTURE_AREA_SIZE}
                  isDisabled={isExporting}
                >
                  <NumberInputField />
                </NumberInput>
              </Box>
            </Grid>
          </FormControl>

          {/* File Name */}
          <FormControl>
            <FormLabel mt={2}>
              <FormattedMessage id="map.export_filename" defaultMessage="File Name" />
            </FormLabel>
            <HStack>
              <Input
                value={settings.fileName}
                onChange={(e) => updateFileName(e.target.value)}
                placeholder="map-export"
                isDisabled={isExporting}
              />
              <Text fontSize="sm" color="brand.border" whiteSpace="nowrap">
                .{settings.format === "jpeg" ? "jpg" : settings.format}
              </Text>
            </HStack>
          </FormControl>
        </RanaAccordionItem>
      </Accordion>
      {/* Export Button */}
      <RanaButton
        m={2}
        mx={4}
        variant="primary"
        onClick={startExport}
        isLoading={isExporting}
        mt={0}
      >
        <FormattedMessage id="map.export" defaultMessage="Export" />
      </RanaButton>
    </VStack>
  );
};
